import { Finding, WorkerStatus, WorkerTrigger } from '../../api/health';
import { QueuePipeline, QueueState } from '../../api/queueMonitor';

/**
 * The two pipelines as one board: Chargebee on the left, Exact on the right, the way there on
 * top and the way back underneath.
 *
 * Built apart from the drawing so the drawing has nothing left to decide. Each lane has the
 * same shape, because both pipelines do: an intake app that receives from the source system
 * and parks the work on a queue, that queue, and a delivery app that reads it and talks to
 * the target system. The intake app is one frame around everything it does - the endpoint,
 * the webhook queue between its two functions, the queue trigger, and whatever else feeds
 * the hand-off queue, like the retry loop or the payment timer. Drawing those as separate
 * badges in one long row is what made the old screen repeat `cbrcv` three times and need a
 * scrollbar per pipeline.
 */

/** A function inside an app, as a node on the board. */
export interface BoardFunction {
    name: string;
    trigger: WorkerTrigger;
    appKey: string;
    /** The queue it reads, for a queue trigger; its findings are the ones to show on it. */
    reads?: string | null;
    /** What feeds this function from outside the board, when that is worth a word. */
    role?: 'payments' | null;
}

export type BoardNode =
    | { kind: 'queue'; queue: QueueState }
    | { kind: 'function'; fn: BoardFunction }
    /** A queue whose messages nothing in the code reads. */
    | { kind: 'nobody'; queue: QueueState };

/** One function app with what it does, in rows: the main chain first, side loops below. */
export interface BoardGroup {
    appKey: string;
    app: WorkerStatus | null;
    rows: BoardRow[];
}

export interface BoardRow {
    /** Which side loop this row is; absent for the main chain. */
    side?: 'retry' | 'payments';
    nodes: BoardNode[];
}

export type LaneItem =
    | { kind: 'group'; group: BoardGroup }
    | { kind: 'queue'; queue: QueueState }
    | { kind: 'nobody'; queue: QueueState };

export interface BoardLane {
    pipeline: QueuePipeline;
    /** Which way the lane runs on screen: Chargebee is always on the left. */
    direction: 'right' | 'left';
    items: LaneItem[];
}

const LANES: { pipeline: QueuePipeline; direction: 'right' | 'left' }[] = [
    { pipeline: 'ChargebeeToExact', direction: 'right' },
    { pipeline: 'ExactToChargebee', direction: 'left' },
];

/** The functions of every app, flattened, so a lookup by queue does not walk the fleet. */
const functionsOf = (workers: WorkerStatus[]) =>
    workers.flatMap((worker) =>
        (worker.functions ?? []).map((fn) => ({ worker, fn }))
    );

/** The function reading a queue, from what the snapshot already knows. */
const consumerNode = (queue: QueueState): BoardNode | null =>
    queue.consumerApp && queue.consumerFunction
        ? {
              kind: 'function',
              fn: {
                  name: queue.consumerFunction,
                  trigger: 'Queue',
                  appKey: queue.consumerApp,
                  reads: queue.name,
              },
          }
        : null;

/**
 * Whether a side queue earns a place on the board.
 *
 * The retry queue is an implementation detail while nothing is on it; drawing it anyway
 * makes the frame taller without saying anything.
 */
const worthShowing = (queue: QueueState, poison?: QueueState) =>
    queue.depth !== 0 || (poison?.depth ?? 0) > 0;

/**
 * The lanes of the board, plus whatever does not fit in them.
 *
 * `workers` may still be empty while the health call is in flight; the board is then drawn
 * from the snapshot alone, which already knows every consumer, and the endpoint and timer
 * nodes appear once the apps are known.
 */
export const buildBoard = (
    queues: QueueState[],
    workers: WorkerStatus[],
    poisonBySource: Map<string, QueueState>
): { lanes: BoardLane[]; others: QueueState[] } => {
    const functions = functionsOf(workers);
    const appOf = (key: string | null | undefined) =>
        workers.find((w) => w.key === key) ?? null;

    const placed = new Set<string>();

    const lanes = LANES.map(({ pipeline, direction }) => {
        const of = (stage: QueueState['stage']) =>
            queues.find((q) => q.pipeline === pipeline && q.stage === stage);

        const webhook = of('Webhook');
        const ready = of('Ready');
        const retry = of('Retry');

        [webhook, ready].forEach((q) => q && placed.add(q.name));

        const groups = new Map<string, BoardGroup>();

        const groupFor = (appKey: string): BoardGroup => {
            let group = groups.get(appKey);

            if (!group) {
                group = { appKey, app: appOf(appKey), rows: [] };
                groups.set(appKey, group);
            }

            return group;
        };

        // The intake: endpoint, webhook queue, the function that reads it.
        if (webhook) {
            const producer = functions.find(
                ({ fn }) =>
                    fn.trigger === 'Http' && fn.writesQueue === webhook.name
            );

            const appKey = webhook.consumerApp ?? producer?.worker.key;

            if (appKey) {
                const nodes: BoardNode[] = [];

                if (producer) {
                    nodes.push({
                        kind: 'function',
                        fn: {
                            name: producer.fn.name,
                            trigger: 'Http',
                            appKey: producer.worker.key,
                        },
                    });
                }

                nodes.push({ kind: 'queue', queue: webhook });
                nodes.push(
                    consumerNode(webhook) ?? { kind: 'nobody', queue: webhook }
                );

                groupFor(appKey).rows.push({ nodes });
            }
        }

        // The retry loop: only while something is on it.
        if (retry && worthShowing(retry, poisonBySource.get(retry.name))) {
            const appKey = retry.consumerApp ?? webhook?.consumerApp;

            if (appKey) {
                placed.add(retry.name);

                groupFor(appKey).rows.push({
                    side: 'retry',
                    nodes: [
                        { kind: 'queue', queue: retry },
                        consumerNode(retry) ?? { kind: 'nobody', queue: retry },
                    ],
                });
            }
        }

        // Timers that feed the hand-off queue directly. In Exact to Chargebee this is the
        // route payments take, and it is the one the old board left out.
        if (ready) {
            functions
                .filter(
                    ({ fn }) =>
                        fn.trigger === 'Timer' && fn.writesQueue === ready.name
                )
                .forEach(({ worker, fn }) => {
                    groupFor(worker.key).rows.push({
                        side: 'payments',
                        nodes: [
                            {
                                kind: 'function',
                                fn: {
                                    name: fn.name,
                                    trigger: 'Timer',
                                    appKey: worker.key,
                                    role: 'payments',
                                },
                            },
                        ],
                    });
                });
        }

        const items: LaneItem[] = Array.from(groups.values()).map((group) => ({
            kind: 'group',
            group,
        }));

        if (ready) {
            items.push({ kind: 'queue', queue: ready });

            if (ready.consumerApp && ready.consumerFunction) {
                items.push({
                    kind: 'group',
                    group: {
                        appKey: ready.consumerApp,
                        app: appOf(ready.consumerApp),
                        rows: [{ nodes: [consumerNode(ready) as BoardNode] }],
                    },
                });
            } else {
                items.push({ kind: 'nobody', queue: ready });
            }
        }

        return { pipeline, direction, items };
    }).filter((lane) => lane.items.length > 0);

    // Everything the two lanes do not account for: a queue of a pipeline this board does not
    // know, or failed messages of a step that is not drawn. They must not quietly disappear.
    // An empty retry queue is left out on purpose, and so is a poison queue whose step is on
    // the board, since its count already hangs under that step.
    const others = queues.filter((q) => {
        if (placed.has(q.name)) {
            return false;
        }

        if (q.stage === 'Poison') {
            return q.depth > 0 && !placed.has(q.poisonOf ?? '');
        }

        return q.stage !== 'Retry' || q.depth !== 0;
    });

    return { lanes, others };
};

/** The findings about one app as a whole, as opposed to about one of its queues. */
export const appFindings = (findings: Finding[], appKey: string) =>
    findings.filter((f) => f.worker === appKey && !f.queue);

/** The findings about one queue. They belong on the queue and on the function reading it. */
export const queueFindings = (
    findings: Finding[],
    queue: string | null | undefined
) => (queue ? findings.filter((f) => f.queue === queue) : []);
